import { useSearchParams } from 'react-router-dom'

import { Box, Grid, Link, Prose, Spinner, Text } from '@factorialco/deprecated-design-system'
import { PerformanceTargetInsightsDocument } from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { AiTag } from 'modules/performance_process/components/insights/AiTag'
import { InsightsEmptyState } from 'modules/performance_process/components/insights/EmptyState'
import { scoreTranslations } from 'modules/performance_process/components/insights/TargetCard'
import { TargetInsightsDetails } from 'modules/performance_process/components/insights/TargetInsight/Details'
import { InsightsEngagementSignals } from 'modules/performance_process/components/insights/common/InsightEngagementSignals'
import { InsightMetricCard } from 'modules/performance_process/components/insights/common/InsightMetricCard'
import { InsightSummary } from 'modules/performance_process/components/insights/common/InsightSummary'

interface Props {
  reviewId: string
  targetId: string
}

export const TargetInsights: React.FC<Props> = ({ reviewId, targetId }) => {
  const { company } = useCurrentSession()
  const { data, loading, error } = useQuery(PerformanceTargetInsightsDocument, {
    variables: { reviewId: reviewId, targetId: targetId, companyId: company.id },
  })
  const [searchParams, setSearchParams] = useSearchParams()

  const isSidepanelOpen = searchParams.get('insightsPanel') === 'open'
  const isDevelopmentModeEnabled = searchParams.get('developmentMode') === 'true'

  const reviewProcess = data?.performance.reviewProcess
  const target = reviewProcess?.materializeTargetsConnection.nodes[0]
  const insight = target?.insight

  const openPanel = () => setSearchParams({ insightsPanel: 'open' })

  const hasInsights = insight && !insight.empty

  const engagementSignalsEnabled =
    data?.features.featuresConnection.edges.find(
      (edge) => edge.node.name === 'dev_performance_engagement_signals'
    )?.node.enabled || isDevelopmentModeEnabled

  const EmptyState = () => {
    if (insight?.empty) {
      return <InsightsEmptyState type="noData" />
    }
    if (reviewProcess?.migratedFromV1) {
      return <InsightsEmptyState type="migratedFromV1" />
    }
    return <InsightsEmptyState type="inProgress" />
  }

  if (loading || error) {
    return (
      <Box justifyContent="center" alignItems="center" paddingY="s48" height="full">
        <Spinner />
      </Box>
    )
  }

  if (!hasInsights) {
    return <EmptyState />
  }

  return (
    <Box gap="s32">
      <AiTag />
      <Grid variant="fixed">
        {insight?.alignmentScore && (
          <InsightMetricCard
            title={i18n.t('performance_process.targets.insights.alignment')}
            description={scoreTranslations(insight.alignmentScore)}
            value={insight.alignmentScore}
            onClick={openPanel}
          />
        )}
        {engagementSignalsEnabled && insight?.engagementSignals && (
          <InsightsEngagementSignals engagementSignals={insight.engagementSignals} />
        )}
      </Grid>

      <InsightSummary title={i18n.t('performance_process.targets.insights.strengths')}>
        <Prose fullWidth>
          <Box gap="s12">
            {insight.strengths !== '' && (
              <Text.Normal lineClamp={3}>{insight.strengths}</Text.Normal>
            )}
            <Link.Touchable onClick={openPanel}>
              {i18n.t('performance_process.targets.insights.see_more')}
            </Link.Touchable>
          </Box>
        </Prose>
      </InsightSummary>

      <Sidepanel
        open={isSidepanelOpen}
        onClose={() => setSearchParams({})}
        width="wide"
        title={i18n.t('performance_process.targets.insights.title')}
      >
        {target && <TargetInsightsDetails target={target} />}
      </Sidepanel>
    </Box>
  )
}
